import React, { useEffect } from 'react';
import clsx from 'clsx';
import moment from 'moment-timezone';
import styles from './PrintAppointmentBoarding.module.scss';
import { faMoon } from '@fortawesome/free-solid-svg-icons/faMoon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PrintBookingDetailAppointmentViewModel } from '../utils/PrintBookingDetailViewModel';
import FeedingsAndMedicationsInfo from 'components/CreateBooking/MultiplePets/FeedingsAndMedicationsInfo';
import { useApiClientWrapper, useMasterData } from 'hooks';
import { Room } from '@spike/room-model';
import { fetchThunk as fetchRoomsThunk, RoomsStatus } from '@spike/rooms-action';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { PageResult } from '@spike/model';
import { RadioLine } from '../PrintAppointmentDaycare/PrintAppointmentDaycare';

type Props = {
    appointment: PrintBookingDetailAppointmentViewModel;
    dateLabel?: string;
    className?: string;
    showDivider?: boolean;
};

const safeJoin = (parts: Array<any>) => parts.filter(Boolean).join(', ');

const FEEDING_MOMENT_COLUMNS = [
    { id: 1, label: 'Morning' },
    { id: 2, label: 'Afternoon' },
    { id: 3, label: 'Evening' }
];

const getConfiguredFeedingColumns = (momentTypes: unknown) => {
    const ids = new Set(
        (Array.isArray(momentTypes) ? momentTypes : []).map(id => Number(id)).filter(id => Number.isFinite(id))
    );
    const configured = FEEDING_MOMENT_COLUMNS.filter(column => ids.has(column.id));
    return configured.length > 0 ? configured : FEEDING_MOMENT_COLUMNS;
};

const PrintAppointmentBoarding: React.FC<Props> = ({ appointment, dateLabel, className, showDivider }) => {
    const md = useMasterData();

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const rooms = useSelector<RootState, PageResult<Room>>(state => state.rooms.rooms);
    const roomsStatus = useSelector<RootState, RoomsStatus>(state => state.rooms.status);

    const range = `${appointment.fromLabel ?? ''} – ${appointment.toLabel ?? ''}`;
    const nights = appointment.nightsCount;
    const description = `${appointment.fromLabel ?? ''}`;

    const feedingSchedules = appointment.feedingSchedules ?? [];
    const hasFeedings = feedingSchedules.length > 0;
    const medications = appointment.medications ?? [];
    const hasMedications = medications.length > 0;

    useEffect(() => {
        dispatch(fetchRoomsThunk(apiClientWrapper, 1, 1000));
    }, []);

    const getRoomDisplayName = (appointment: any): string => {
        try {
            const rawAppt = appointment?.raw?.appointment || {};

            // 1. Identificar el ID de la unidad (soporta múltiples formatos de API)
            const roomUnitId = rawAppt.roomUnitId || rawAppt.room_unit_id || (rawAppt.room && rawAppt.room.unitId);

            // 2. Buscar en las rooms cargadas en redux (selector `rooms`)
            const roomList = rooms?.page || [];
            if (roomUnitId && roomList.length) {
                for (const room of roomList) {
                    const unit = room.units?.find((u: any) => u.id === roomUnitId);
                    if (unit) return `${room.name} - ${unit.name}`;
                }
            }

            // 3. Fallback: Datos directos del objeto room si no se encontró en el listado
            if (rawAppt.room?.name) {
                const roomName = rawAppt.room.name;
                const unitName = rawAppt.room.unit?.name || rawAppt.room.unitName || rawAppt.roomUnitName;
                return unitName ? `${roomName} - ${unitName}` : roomName;
            }

            return ''; // Retorna vacío si no hay información de habitación
        } catch (e) {
            return '';
        }
    };

    const roomLabel = getRoomDisplayName(appointment);

    // Build per-day rows for the booking range (inclusive of start and end dates)
    const dateRows: string[] = [];
    const dateMoments: Array<moment.Moment> = [];
    try {
        const from = (appointment as any).raw?.appointment?.duration?.from;
        const to = (appointment as any).raw?.appointment?.duration?.to;
        if (from && to) {
            const start = moment(from).clone().startOf('day');
            const end = moment(to).clone().startOf('day');
            const totalDays = end.diff(start, 'days');
            for (let i = 0; i <= totalDays; i++) {
                const cur = start.clone().add(i, 'days');
                dateRows.push(cur.format('ddd, MM/DD'));
                dateMoments.push(cur);
            }
        }
    } catch (e) {
        // fallback: empty
    }

    return (
        <div className={clsx(styles.root, className, { [styles.withDivider]: showDivider })}>
            <div className={styles.header}>
                <div className={styles.appointmentRange}>
                    <span className={styles.rangeText}>{range}</span>

                    <div className={styles.nightsBadge}>
                        <FontAwesomeIcon
                            icon={faMoon}
                            className={styles.moonIcon}
                        />
                        <span className={styles.nightsCount}>
                            {nights} {nights === 1 ? 'Night' : 'Nights'}
                        </span>
                    </div>
                </div>
            </div>

            <div className={styles.service}>{appointment.serviceName ?? ''}</div>

            <div className={styles.subsectionHeader}>
                ROOM: <span className={styles.description}>{roomLabel || `${nights}`}</span>
            </div>

            <FeedingsAndMedicationsInfo
                hasFeedings={hasFeedings}
                hasMedications={hasMedications}
            />

            <div className={styles.subsection}>
                {feedingSchedules.length === 0 ? (
                    <div className={styles.subsectionTitle}>No Feeding</div>
                ) : (
                    feedingSchedules.map((fs: any, sIdx: number) => {
                        // header text for this feeding schedule
                        const provisioner = md.foodProvisionTypes?.find(
                            (p: any) => p.id === fs.foodProvisionTypeId
                        )?.name;
                        const foodType = md.foodTypes?.find((f: any) => f.id === fs.foodTypeId)?.name;
                        const unit = md.foodQuantityUnits?.find((u: any) => u.id === fs.quantity?.unit)?.name;
                        const qty = fs.quantity?.quantity ? `${fs.quantity.quantity} ${unit ?? ''}`.trim() : undefined;
                        const providerText =
                            provisioner?.toLowerCase() === 'client' ? 'Client provides food' : provisioner ?? '';
                        const headerText = safeJoin([providerText, foodType, fs.brand, qty]);
                        const feedingColumns = getConfiguredFeedingColumns(fs.momentTypes);

                        return (
                            <div
                                key={sIdx}
                                className={styles.scheduleBlock}
                            >
                                <div className={styles.subsectionTitle}>{`Feeding Schedule ${sIdx + 1}`}</div>

                                <div className={styles.scheduleLabel}>{headerText}</div>
                                {fs.feedingInstructions ? (
                                    <div className={styles.instructionsLabel}>
                                        FEEDING INSTRUCTIONS:{' '}
                                        <span className={styles.instructions}>{fs.feedingInstructions}</span>
                                    </div>
                                ) : null}

                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Dates</th>
                                            {feedingColumns.map(column => (
                                                <th key={column.id}>{column.label}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dateRows.length === 0 ? (
                                            <tr>
                                                <td colSpan={feedingColumns.length + 1}>Not provided</td>
                                            </tr>
                                        ) : (
                                            dateRows.map((d, idx) => {
                                                return (
                                                    <tr key={idx}>
                                                        <td>{d}</td>
                                                        {feedingColumns.map(column => (
                                                            <td key={column.id}></td>
                                                        ))}
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })
                )}
            </div>

            <div className={styles.subsection}>
                <div className={styles.subsectionTitleMedication}>Medications</div>

                <table className={styles.medTable}>
                    <thead>
                        <tr>
                            <th style={{ width: '15%' }}>Dates</th>
                            <th style={{ width: '45%' }}>Medication</th>
                            <th style={{ width: '40%' }}>Doses</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dateRows.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={3}
                                    className={styles.emptyCell}
                                >
                                    Not provided
                                </td>
                            </tr>
                        ) : (
                            dateRows.map((d, idx) => {
                                const mDate = dateMoments[idx];
                                const dateKey = mDate.format('YYYY-MM-DD');
                                const medsForDateObj = appointment.groupedMedications?.[dateKey] || {};
                                const medsForDate = Object.values(medsForDateObj);
                                const medCount = medsForDate.length || 1;

                                if (medsForDate.length === 0) {
                                    return (
                                        <tr
                                            key={idx}
                                            className={styles.mainRow}
                                        >
                                            <td className={styles.dateCell}>{d}</td>
                                            <td className={styles.medCell}>
                                                <span className={styles.emptyText}>Not provided</span>
                                            </td>
                                            <td className={styles.dosesCell}>
                                                <span className={styles.emptyText}>Not provided</span>
                                            </td>
                                        </tr>
                                    );
                                }

                                return medsForDate.map((med, mi) => (
                                    <tr
                                        key={`${idx}-${mi}`}
                                        className={clsx(styles.mainRow, {
                                            [styles.firstMedRow]: mi === 0,
                                            [styles.lastMedRow]: mi === medCount - 1
                                        })}
                                    >
                                        {mi === 0 && (
                                            <td
                                                className={styles.dateCell}
                                                rowSpan={medCount}
                                            >
                                                {d}
                                            </td>
                                        )}
                                        <td className={styles.medCell}>
                                            <div className={styles.medName}>{med.name}</div>
                                            {med.instructions && (
                                                <div className={styles.medInstructions}>{med.instructions}</div>
                                            )}
                                        </td>
                                        <td className={styles.dosesCell}>
                                            <div className={styles.dosesGrid}>
                                                {med.times.map((t, doseIdx) => (
                                                    <RadioLine
                                                        key={doseIdx}
                                                        variant="medicationStyle"
                                                        momentLabel={`DOSE ${doseIdx + 1}`}
                                                        mainText={t}
                                                    />
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ));
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PrintAppointmentBoarding;
